import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, SxProps, Tooltip } from '@mui/material';
import { Replay } from '@mui/icons-material';

import WordCloudContent from "./WordCloudContent";
import { WordCloudSettings } from "./WordCloudSettings";
import WidgetTitle from "components/widgetParts/WidgetTitle";
import WidgetLayout from "components/widgetParts/WidgetLayout";
import { wordCloudDefaultSettings } from "components/widgets/charts/WordCloud/WordCloudUtils";
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { getSingleOriginalRequestByName, getSingleWidgetResponseByName } from 'system/widget/Widget';

export const generateSeed = () => Math.floor(Math.random() * 1000);

function WordCloud(props: WithQueryWidgetProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const wordCloudSettings = { ...props.widget?.data?.settings } as WordCloudSettings;
  const [seed, setSeed] = useState(generateSeed());

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'word-cloud');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'word-cloud');

  const cornerOverlaySX: SxProps = {
    position: "absolute",
    top: "auto !important",
    bottom: "0px !important",
    right: "0px !important",
    borderRadius: "10px",
    width: "42px !important",
    height: "42px !important",
    padding: "5px",
    opacity: "0",
    transition: "all .15s",
    zIndex: "9999999999 !important",
    "&:hover, &:active": {
      backdropFilter: "blur(12px) contrast(90%) grayscale(20%)",
      opacity: "1",
      transition: "all .15s",
    },
  };

  const currentState = {
    ...wordCloudDefaultSettings,
    ...props.widget?.data,
    ...props.widget?.data?.settings
  };

  const refreshButton = (
    <Box sx={cornerOverlaySX} className="drag-cancel react-grid-item-overlay-corner">
      <Tooltip title={i18n.t("refresh")}>
        <IconButton
          size='small'
          aria-label={i18n.t("refresh")}
          onClick={() => setSeed(generateSeed())}
        >
          <Replay />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <WidgetLayout
      header={
        <WidgetTitle
          alignment={currentState.widgetTitleAlignment}
          defaultTitle={i18n.t("widget-types.word-cloud.label")}
          customTitle={currentState.customWidgetTitle}
          settingsTitle={currentState.widgetTitle}
          widgetQueryTitle={apiResponseInfo?.queryDisplayTitle} />
      }>
      {!props.isInLayoutMode && <>{refreshButton}</>}
      <WordCloudContent
        apiResponses={apiResponseInfo?.responseBody}
        activeQueryIds={apiResponseInfo?.originatingRequest?.activeQueryIds}
        queryInfo={props.queryInfo}
        widget={props.widget}
        contentRef={props.contentRef}
        seed={seed}
        wordCloudSettings={wordCloudSettings} />
    </WidgetLayout>
  );
}

export default withQueryWidget(WordCloud, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});
